import type { Prayer } from '@/types/prayer';
import { buildRelatedContext, findRelatedPrayers } from '@/lib/search-prayers';
import { existingPeopleList, peopleInPrayer } from '@/lib/prayer-utils';
import {
  prayerOutboundExpectation,
  type PrayerOutboundExpectation,
} from '@/lib/outbound-prayer-authorization';

export type AutomaticEnrichmentContext = {
  relatedContext: string;
  existingPeople: string[];
  contextExpectations: PrayerOutboundExpectation[];
};

export const MAX_AUTOMATIC_ENRICHMENTS_PER_PASS = 20;

/**
 * Build automatic cross-prayer context from the owner's other prayers. The
 * target is excluded from its own context. Every contributor carries its exact
 * revision so the rule #90 wire boundary can recheck it after auth resolves.
 */
export function buildPrivacySafeEnrichmentContext(
  sourceText: string,
  prayers: Prayer[],
  targetId: string,
): AutomaticEnrichmentContext {
  const safeOthers = prayers.filter(
    (prayer) => prayer.id !== targetId,
  );
  const related = findRelatedPrayers(sourceText, safeOthers);
  const existingPeople = existingPeopleList(safeOthers);
  const contributorById = new Map<string, Prayer>();
  for (const prayer of related) contributorById.set(prayer.id, prayer);
  for (const name of existingPeople) {
    const key = name.trim().toLocaleLowerCase();
    const representative = safeOthers.find((prayer) =>
      peopleInPrayer(prayer).some(
        (candidate) => candidate.trim().toLocaleLowerCase() === key,
      ),
    );
    if (representative) contributorById.set(representative.id, representative);
  }
  return {
    relatedContext: buildRelatedContext(related),
    existingPeople,
    contextExpectations: [...contributorById.values()].map((prayer) =>
      prayerOutboundExpectation(prayer),
    ),
  };
}
